import { SizeType } from "antd/es/config-provider/SizeContext";
import cn from "classnames";
import { useEffect, useMemo, useState } from "react";
import PhoneInputComponent, { CountryData } from "react-phone-input-2";
import idLang from "react-phone-input-2/lang/id.json";
import ruLang from "react-phone-input-2/lang/ru.json";
import "react-phone-input-2/lib/style.css";
import { isValidPhoneNumber } from "react-phone-number-input";

import { geoLocator } from "@app/shared/lib/geo-locator";
import { useLang } from "@app/shared/lib/hooks";
import { isEmpty } from "@app/shared/lib/is-empty";

import ukLang from "./lang/uk.json";

import styles from "./PhoneInput.module.scss";

export { isValidPhoneNumber };

type PhoneInputProps = {
  controlRef?: React.Ref<HTMLInputElement>;
  size?: SizeType;
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (
    phone: string,
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    country: {} | CountryData,
    e: React.ChangeEvent<HTMLInputElement>,
    formatValue: string,
  ) => void;
  onMounted?: (
    phone: string,
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    country: {} | CountryData,
    formatValue: string,
  ) => void;
  className?: string;
  autoFetchCountry?: boolean;
  addPlusSign?: boolean;
};

export const validatePhoneNumber = (
  value: string | undefined,
  required: boolean,
  errorMessages: { required: string; invalid: string },
) => {
  if (!required && (!value || value.trim() === "")) return undefined;

  if (required && (!value || value.trim() === ""))
    return errorMessages.required;

  if (value && value.trim() !== "") {
    const formattedNumber = value.startsWith("+") ? value : `+${value}`;
    if (!isValidPhoneNumber(formattedNumber)) {
      return errorMessages.invalid;
    }
  }

  return undefined;
};

export const PhoneInput: React.FC<PhoneInputProps> = ({
  controlRef,
  autoFetchCountry = true,
  size = "middle",
  className,
  addPlusSign = false,
  disabled,
  onChange,
  onMounted,
  placeholder,
  value,
}) => {
  const { lang } = useLang();
  const [country, setCountry] = useState<string>();

  const loc = useMemo(() => {
    switch (lang) {
      case "id":
        return idLang;
      case "ru":
        return ruLang;
      case "uk":
        return ukLang;
      case "en":
      default:
        return undefined;
    }
  }, [lang]);

  useEffect(() => {
    (async () => {
      if (!autoFetchCountry) return;
      const countryString = await geoLocator.fetchCounty();
      setCountry(countryString?.toLowerCase());
    })();
  }, [autoFetchCountry]);

  if (autoFetchCountry && !country) return null;

  return (
    <PhoneInputComponent
      country={country}
      value={value}
      inputProps={{ ref: controlRef }}
      disabled={disabled}
      // eslint-disable-next-line max-params
      onChange={(changedValue, data, event, format) => {
        const resultValue =
          !isEmpty(changedValue) && addPlusSign === true
            ? `+${changedValue}`
            : changedValue;
        onChange?.(resultValue, data, event, format);
      }}
      onMount={(mountedValue, data, format) => {
        const resultValue =
          !isEmpty(mountedValue) && addPlusSign === true
            ? `+${mountedValue}`
            : mountedValue;
        onMounted?.(resultValue, data, format);
      }}
      placeholder={placeholder}
      containerClass={cn(styles.phone_input, className, `phone_input_${size}`)}
      data-size={size}
      localization={loc}
    />
  );
};
